import { urlFor } from "~/client";
import { Button } from "~/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "~/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useNavLauncherStore } from "~/components/zustand/nav-launcher-store";
import { navIcon } from "~/constants/nav-catalog";
import { cn } from "~/lib/utils";
import type { Data } from "@generated/data";
import { router } from "@inertiajs/react";
import { ChevronRight, LayoutGrid, Pencil, Pin, PinOff, Settings } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Forma mínima que el servidor envía por cable (espejo de `ResolvedNavItem`). Se
// deriva del prop compartido `navigation` para no duplicar el contrato.
//
// `inertia.always()` marca la prop como AlwaysProp<T> en el tipo generado, y como
// además puede ser `undefined` (guest/no-company) el inferidor la deja envuelta
// (`{ value: T }`) en lugar de desenvolverla. En RUNTIME Inertia entrega el valor
// desenvuelto, así que desenvolvemos aquí a nivel de tipo. `UnwrapAlways` es un
// no-op si el prop ya viene desenvuelto (compat. hacia adelante).
type UnwrapAlways<T> = T extends { value: infer V } ? V : T;
// Doble `NonNullable`: el prop puede ser `undefined` (guest/no-company) y, según
// cómo el inferidor envuelva el AlwaysProp, el `undefined` puede quedar DENTRO del
// valor (`{ value: ResolvedNav | undefined }`). Lo quitamos antes y después de
// desenvolver para que `["bar"]`/`["launcher"]` indexen sobre la forma real.
export type ResolvedNav = NonNullable<UnwrapAlways<NonNullable<Data.SharedProps["navigation"]>>>;
export type ResolvedNavItem = ResolvedNav["bar"][number];

// Claves de indicador en vivo (dot) que expone `navigationIndicators`.
export type NavigationIndicatorKey =
  | "chatHasUnread"
  | "emailHasUnread"
  | "socialCommentsHasUnread"
  | "formsHasUnread"
  | "ticketsHasOpen"
  | "supportHasOpen"
  | "dealsCopilotHasPending";
export type NavigationIndicators = Record<NavigationIndicatorKey, boolean | null>;

// Los destinos `settings-*` son subpáginas de Configuración: viven SOLO en el
// Lanzador (grupo "Configuración"), nunca como pestañas de la barra.
export const isSettingsNavItem = (item: Pick<ResolvedNavItem, "id">): boolean =>
  item.id.startsWith("settings-");

// `urlFor` está tipado sobre la unión literal de nombres de ruta conocidos, pero
// los items de navegación transportan su ruta como string decidido en servidor
// (el catálogo es la fuente de la verdad). Estrechamos una vez a una firma que
// acepta string: el resolver solo emite rutas del catálogo.
const urlForNav = urlFor as (routeName: string, params?: Record<string, string>) => string;

export function navItemHref(item: ResolvedNavItem): string {
  const base = urlForNav(item.routeName, item.params);
  if (item.query && Object.keys(item.query).length > 0) {
    const search = new URLSearchParams(item.query).toString();
    return `${base}?${search}`;
  }
  return base;
}

// Delta de estado deseado COMPLETO: todo lo de `desiredVisibleIds` queda visible y
// ordenado; el resto del set permitido queda oculto. Round-trip por el resolver:
// `shown` fuerza visibilidad (incluso sobre company.hidden), `hidden` la quita,
// `order` ordena. Expresa la intención del usuario sin ambigüedad.
export function navPreferencesPayload(
  desiredVisibleIds: string[],
  launcherIds: string[],
): { order: string[]; hidden: string[]; shown: string[] } {
  return {
    order: desiredVisibleIds,
    hidden: launcherIds.filter((id) => !desiredVisibleIds.includes(id)),
    shown: desiredVisibleIds,
  };
}

type NavLauncherProps = {
  // Todos los destinos permitidos (set exhaustivo), en orden de catálogo.
  launcher: ResolvedNavItem[];
  // Barra tal cual la resuelve el servidor (`navigation.bar`, verbatim). Es la
  // fuente de verdad del estado "fijado" (pin lleno) y del delta de pin/unpin.
  bar: ResolvedNavItem[];
  navigationIndicators: NavigationIndicators | undefined;
  // El destino "internal" no tiene indicatorKey de servidor; su no-leído se
  // calcula en el cliente y se pasa aquí para paridad del dot con la barra.
  internalHasUnread: boolean;
};

export function NavLauncher({
  launcher,
  bar,
  navigationIndicators,
  internalHasUnread,
}: NavLauncherProps) {
  const { t } = useTranslation(["home_common", "home_settings_setting"]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Estado deseado optimista para acumular pins rápidos mientras hay PATCH en vuelo.
  const pendingDesiredRef = useRef<string[] | null>(null);
  const inFlightRef = useRef(0);
  const settingsGroupExpanded = useNavLauncherStore((state) => state.settingsGroupExpanded);
  const setSettingsGroupExpanded = useNavLauncherStore((state) => state.setSettingsGroupExpanded);
  const setEditMode = useNavLauncherStore((state) => state.setEditMode);

  const allAppsLabel = t("nav_launcher.all_apps", "Todas las apps");
  const pinLabel = t("nav_launcher.pin", "Fijar en la barra");
  const unpinLabel = t("nav_launcher.unpin", "Quitar de la barra");

  const barIds = useMemo(() => new Set(bar.map((item) => item.id)), [bar]);
  const launcherIds = useMemo(() => launcher.map((item) => item.id), [launcher]);

  const labelOf = (item: ResolvedNavItem): string =>
    t(item.labelKey, {
      ns: isSettingsNavItem(item) ? "home_settings_setting" : "home_common",
      defaultValue: item.labelKey,
    });

  const itemHasIndicator = (item: ResolvedNavItem): boolean => {
    if (item.id === "internal") {
      return internalHasUnread;
    }
    if (!item.indicatorKey) {
      return false;
    }
    return navigationIndicators?.[item.indicatorKey as NavigationIndicatorKey] === true;
  };

  const primaryItems = useMemo(
    () => launcher.filter((item) => !isSettingsNavItem(item)),
    [launcher],
  );
  const settingsItems = useMemo(() => launcher.filter(isSettingsNavItem), [launcher]);

  const normalizedQuery = query.trim().toLowerCase();
  const searching = normalizedQuery.length > 0;
  const matchesQuery = (item: ResolvedNavItem): boolean =>
    !searching || labelOf(item).toLowerCase().includes(normalizedQuery);

  const primaryFiltered = primaryItems.filter(matchesQuery);
  const settingsFiltered = settingsItems.filter(matchesQuery);
  // Sección "Ocultos de tu barra": destinos permitidos (launcher) que NO están en
  // la barra y tienen actividad en vivo (dot). Solo se muestra sin búsqueda activa
  // para no duplicar filas con el grupo principal.
  const hiddenWithIndicator = searching
    ? []
    : launcher.filter((item) => !barIds.has(item.id) && itemHasIndicator(item));

  const navigateTo = (item: ResolvedNavItem): void => {
    router.visit(navItemHref(item));
    setOpen(false);
  };

  const togglePin = (item: ResolvedNavItem): void => {
    // Mitiga la carrera de clics rápidos: si hay PATCH(s) en vuelo, la barra `bar`
    // (verdad del servidor) aún no refleja el pin anterior, así que basamos el
    // siguiente delta en el ÚLTIMO estado deseado enviado (ref optimista) en lugar
    // del prop. Así N pins rápidos se ACUMULAN en vez de pisarse. Cuando no hay
    // nada en vuelo, `pendingDesiredRef` es null y volvemos a confiar en `bar`.
    const base = pendingDesiredRef.current ?? bar.map((entry) => entry.id);
    const pinned = base.includes(item.id);
    const desired = pinned ? base.filter((id) => id !== item.id) : [...base, item.id];
    pendingDesiredRef.current = desired;
    inFlightRef.current += 1;
    // No cerramos el popover: la barra se actualiza detrás vía el prop
    // `navigation` (siempre fresco) al volver del redirect.
    router.patch(urlFor("nav-preferences.update"), navPreferencesPayload(desired, launcherIds), {
      preserveScroll: true,
      preserveState: true,
      onFinish: () => {
        inFlightRef.current -= 1;
        // Solo cuando TODO ha aterrizado soltamos el estado optimista y volvemos a
        // leer del prop `bar` (ya repintado con la verdad del servidor).
        if (inFlightRef.current <= 0) {
          inFlightRef.current = 0;
          pendingDesiredRef.current = null;
        }
      },
    });
  };

  const renderRow = (item: ResolvedNavItem, keyPrefix?: string) => {
    const Icon = navIcon(item.iconName);
    const pinned = barIds.has(item.id);
    // cmdk keys selection by `value`; the "Ocultos" section repeats ids from the
    // main group, so namespace the value (and React key) to keep them unique.
    const rowKey = keyPrefix ? `${keyPrefix}-${item.id}` : item.id;
    return (
      <CommandItem key={rowKey} value={rowKey} onSelect={() => navigateTo(item)}>
        <Icon className="size-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate">{labelOf(item)}</span>
        {itemHasIndicator(item) && (
          <span className="bg-primary size-2 shrink-0 rounded-full" aria-hidden="true" />
        )}
        <button
          type="button"
          aria-label={pinned ? unpinLabel : pinLabel}
          title={pinned ? unpinLabel : pinLabel}
          className="text-muted-foreground hover:text-foreground shrink-0 rounded-sm p-0.5 opacity-0 transition group-hover/command-item:opacity-100 group-data-[selected=true]/command-item:opacity-100"
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            togglePin(item);
          }}
        >
          {pinned ? <Pin className="size-3.5 fill-current" /> : <PinOff className="size-3.5" />}
        </button>
      </CommandItem>
    );
  };

  const hasAnyResult = primaryFiltered.length > 0 || settingsFiltered.length > 0;

  return (
    <div className="border-border/60 ml-0.5 flex items-center border-l pl-1">
      <Popover
        open={open}
        onOpenChange={(next: boolean) => {
          setOpen(next);
          if (!next) {
            setQuery("");
          }
        }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                className="text-muted-foreground hover:text-foreground hover:bg-background h-auto rounded-md border-transparent px-3 py-1.5 font-medium"
                aria-label={allAppsLabel}
              >
                <LayoutGrid className="size-4" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>{allAppsLabel}</TooltipContent>
        </Tooltip>
        <PopoverContent align="end" className="w-80 p-0">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t("nav_launcher.search_placeholder", "Buscar destino…")}
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              {!hasAnyResult && (
                <div className="text-muted-foreground py-6 text-center text-sm">
                  {t("nav_launcher.empty", "Sin resultados.")}
                </div>
              )}

              {hiddenWithIndicator.length > 0 && (
                <CommandGroup heading={t("nav_launcher.hidden_from_bar", "Ocultos de tu barra")}>
                  {hiddenWithIndicator.map((item) => renderRow(item, "hidden"))}
                </CommandGroup>
              )}

              {primaryFiltered.length > 0 && (
                <CommandGroup heading={t("nav_launcher.apps", "Aplicaciones")}>
                  {primaryFiltered.map((item) => renderRow(item))}
                </CommandGroup>
              )}

              {settingsItems.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    {!searching && (
                      <CommandItem
                        value="__settings-group-toggle__"
                        onSelect={() => setSettingsGroupExpanded(!settingsGroupExpanded)}
                      >
                        <Settings className="size-4 shrink-0" />
                        <span className="min-w-0 flex-1 truncate">
                          {t("nav_launcher.settings_group", "Configuración")}
                        </span>
                        <ChevronRight
                          className={cn(
                            "size-4 shrink-0 transition-transform",
                            settingsGroupExpanded && "rotate-90",
                          )}
                        />
                      </CommandItem>
                    )}
                    {(searching || settingsGroupExpanded) &&
                      settingsFiltered.map((item) => renderRow(item, "settings"))}
                  </CommandGroup>
                </>
              )}

              {!searching && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      value="__personalize-bar__"
                      onSelect={() => {
                        setEditMode(true);
                        setOpen(false);
                      }}
                    >
                      <Pencil className="size-4 shrink-0" />
                      <span className="min-w-0 flex-1 truncate">
                        {t("nav_editor.personalize", { defaultValue: "Personalizar barra…" })}
                      </span>
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
